/**
 * The right-edge **quest tracker** (Cosmetic Progression Slice D, PRD #101 /
 * ADR-0044, ADR-0049, restyled by ADR-0283): a collapsible DOM gump showing a progress bar, a numeric
 * "M:SS to next unlock" countdown, and the player's own total online time.
 *
 * It is a sibling overlay of the chat and the Top Banksitters window - a
 * fixed-position element over the `#app` canvas, not a Pixi in-world surface - so it
 * reuses the same DOM idiom every other piece of britbank chrome uses (the markup
 * and styling live in `index.html`; this module owns only the wiring). It reads no
 * Pixi and no socket: `main.ts` feeds it the server's authoritative `totalOnlineMs`
 * anchors (via {@link HudHandle.setOnlineTime}) and ticks it each render frame (via
 * {@link HudHandle.sync}).
 *
 * **Server-anchored, client-ticked** (the leaderboard's honesty bound): the server
 * pushes the true `totalOnlineMs` on login and at each accrual checkpoint, and the
 * client *extrapolates it forward at wall-clock rate between anchors and snaps to
 * each fresh push* - it smooths between server truths, it never invents time. The
 * bar/countdown derive purely from the (extrapolated) tally against the shared
 * {@link UNLOCK_INTERVAL_MS}, so the bar fills smoothly and **wraps/resets on its
 * own each time the tally crosses an unlock boundary**, with no special reset event.
 *
 * The XP/countdown math and the anchor extrapolation are split out as the pure
 * {@link fillFraction}/{@link msToNextUnlock}/{@link formatCountdown} helpers and the
 * {@link HudClock}, so the progression arithmetic is unit-testable without a DOM.
 */
import { UNLOCK_INTERVAL_MS } from './sim/index';
import { formatGold, formatOnlineTime } from './leaderboard';

/**
 * The fraction `0..1` of the *current* unlock interval a character has filled - the
 * XP bar's width. It is the tally's remainder within the interval over the interval
 * length, so it climbs from `0` at a boundary to (just under) `1` and **wraps back
 * to ~0** the instant the tally crosses the next boundary (a fresh unlock). A
 * negative or non-finite tally clamps to `0` so a bad value never paints garbage.
 */
export function fillFraction(totalOnlineMs: number): number {
  if (!Number.isFinite(totalOnlineMs) || totalOnlineMs <= 0) return 0;
  return (totalOnlineMs % UNLOCK_INTERVAL_MS) / UNLOCK_INTERVAL_MS;
}

/**
 * Milliseconds remaining until this character's next unlock - the countdown's
 * source. It is the complement of the in-interval remainder, so it ticks down from
 * a full {@link UNLOCK_INTERVAL_MS} at a boundary toward `0` as the bar fills, then
 * resets to a full interval at the next boundary. Clamped to `(0, interval]` so a
 * non-finite or boundary-exact tally reads a full interval rather than `0`.
 */
export function msToNextUnlock(totalOnlineMs: number): number {
  if (!Number.isFinite(totalOnlineMs) || totalOnlineMs <= 0) return UNLOCK_INTERVAL_MS;
  const within = totalOnlineMs % UNLOCK_INTERVAL_MS;
  return UNLOCK_INTERVAL_MS - within;
}

/**
 * Format a remaining-time duration (ms) as a `M:SS` countdown, e.g. `5:00`, `3:42`,
 * `0:09`. Seconds are **ceil**ed so a freshly-reset interval reads a clean `5:00`
 * and the final second shows `0:01` rather than prematurely flicking to `0:00`;
 * only a fully-elapsed (or negative/non-finite) value reads `0:00`. Minutes are
 * unbounded (the interval is 5min today, but the format is general).
 */
export function formatCountdown(ms: number): string {
  const totalSeconds = Number.isFinite(ms) && ms > 0 ? Math.ceil(ms / 1000) : 0;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * The server-anchor extrapolator (Slice D): holds the last authoritative
 * `totalOnlineMs` the server pushed and the wall-clock instant it arrived, and
 * reports the *extrapolated* tally at any later instant - the anchor plus the
 * wall-clock elapsed since it. {@link setAnchor} **snaps** to a fresh server truth
 * (the value jumps to exactly what the server said, however far the local estimate
 * had drifted); {@link currentMs} between anchors advances at real-time rate, which
 * is correct because the connection is online and therefore accruing. Pure and
 * clock-injected (the caller passes `now`), so the snap/extrapolation is testable
 * without a DOM or a real clock.
 */
export class HudClock {
  private anchorMs = 0;
  private anchorAt = 0;

  /** Snap to a server anchor: `totalOnlineMs` as of wall-clock instant `now`. */
  setAnchor(totalOnlineMs: number, now: number): void {
    this.anchorMs = Number.isFinite(totalOnlineMs) && totalOnlineMs > 0 ? totalOnlineMs : 0;
    this.anchorAt = now;
  }

  /**
   * The extrapolated total online ms at `now`: the last anchor plus the wall-clock
   * elapsed since it. The elapsed term is floored at `0` so a clock that appears to
   * run backwards (or `now` before the anchor) never rewinds the displayed tally.
   */
  currentMs(now: number): number {
    return this.anchorMs + Math.max(0, now - this.anchorAt);
  }
}

/** Options for {@link initHud}; all optional so headless tests can pass none. */
export interface HudOptions {
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
  /**
   * Wall-clock source for anchoring/extrapolation, defaulting to
   * `performance.now()`. Injected so a test drives the extrapolation deterministically
   * (the same role `GameServerOptions.now` plays server-side).
   */
  now?: () => number;
  /** Report quest-gump open-state changes to the shared toolbar panel group. */
  onOpenChange?: (open: boolean) => void;
}

/** The handle `main.ts` drives the quest tracker and toolbar GP readout through. */
export interface HudHandle {
  /** Force the quest gump open or closed through the shared toolbar group. */
  setOpen(open: boolean): void;
  /**
   * Adopt a fresh server online-time anchor (`totalOnlineMs`), snapping the tracker to
   * it and opening the quest gump on the first anchor. Re-renders immediately so the
   * bar/countdown/own-time reflect the new truth without waiting for the next frame.
   */
  setOnlineTime(totalOnlineMs: number): void;
  /**
   * Re-render the bar, countdown, and own-time from the *extrapolated* tally at the
   * current instant. Called each render frame; a no-op until the first anchor
   * arrives (so the tracker shows nothing rather than a fake `0`).
   */
  sync(): void;
  /**
   * Adopt a fresh server gold anchor (Rat Swarm Slice F, PRD #222): the owner-only
   * `GoldUpdate` push (ADR-0119), fed straight through by `main.ts` into the toolbar's
   * GP segment. `durable` remains because it is part of the server push, but the
   * compact toolbar presentation shows only the formatted balance.
   */
  setGold(gold: number, durable: boolean): void;
  /**
   * Suppress (or restore) the tracker while an Arena run is active.
   * `main.ts` suppresses this town quest surface on entry and restores it when the run
   * ends. While suppressed the tracker stays hidden across fresh online-time anchors;
   * restoring re-reveals it only if it had already been anchored (revealed).
   */
  setSuppressed(suppressed: boolean): void;
}

/**
 * Wire the `#hud` quest gump (declared hidden in `index.html`) into a live progress
 * bar + countdown + own-time read-out. The element and its parts (`#hud-fill`,
 * `#hud-countdown`, `#hud-online`) are looked up once; any that are missing are
 * tolerated (the headless HUD tests mount a subset), so a partial-markup client
 * never throws. The tracker stays hidden until the first {@link HudHandle.setOnlineTime}
 * anchor reveals it.
 */
export function initHud(opts: HudOptions = {}): HudHandle {
  const doc = opts.doc ?? document;
  const now = opts.now ?? (() => performance.now());
  const onOpenChange = opts.onOpenChange ?? (() => {});
  const root = doc.getElementById('hud');
  const fill = doc.getElementById('hud-fill');
  const countdown = doc.getElementById('hud-countdown');
  const online = doc.getElementById('hud-online');
  const toolbarGold = doc.getElementById('toolbar-gold');
  const trackerButton = doc.getElementById('quest-tracker-button') as HTMLButtonElement | null;

  const clock = new HudClock();
  let anchored = false;
  // Suppressed while the Arena run HUD borrows the bottom-centre slot: hold the
  // strip hidden even across a fresh online-time anchor, restore on run end.
  let suppressed = false;
  let userOpen = true;
  const syncVisibility = (): void => {
    if (root) root.hidden = suppressed || !anchored || !userOpen;
    trackerButton?.setAttribute('aria-expanded', String(userOpen));
  };
  const setOpen = (open: boolean): void => {
    if (userOpen === open) return;
    userOpen = open;
    syncVisibility();
    onOpenChange(open);
  };
  trackerButton?.addEventListener('click', () => {
    setOpen(!userOpen);
  });

  // Last value written to each element, so an unchanged frame does zero DOM work
  // (item 5 / ADR-0135). `render` runs every rAF frame but the derived strings only
  // change ~once a second (the countdown) or slower (own-time), and the fill width
  // barely moves frame-to-frame; without this gate every frame reassigned three DOM
  // properties (each a style recalc / text reflow) for values almost always identical
  // to the last. `null` means "never written", so the first render always paints.
  let lastFillWidth: string | null = null;
  let lastCountdown: string | null = null;
  let lastOnline: string | null = null;

  const render = (at: number): void => {
    const total = clock.currentMs(at);
    if (fill) {
      const width = `${(fillFraction(total) * 100).toFixed(3)}%`;
      if (width !== lastFillWidth) {
        fill.style.width = width;
        lastFillWidth = width;
      }
    }
    if (countdown) {
      const text = `${formatCountdown(msToNextUnlock(total))} remaining`;
      if (text !== lastCountdown) {
        countdown.textContent = text;
        lastCountdown = text;
      }
    }
    if (online) {
      const text = `${formatOnlineTime(total)} online`;
      if (text !== lastOnline) {
        online.textContent = text;
        lastOnline = text;
      }
    }
  };

  return {
    setOnlineTime(totalOnlineMs) {
      const at = now();
      clock.setAnchor(totalOnlineMs, at);
      const firstAnchor = !anchored;
      anchored = true;
      // Reveal on the first anchor - unless a run is currently borrowing the slot.
      syncVisibility();
      if (firstAnchor) onOpenChange(userOpen);
      render(at);
    },
    setOpen,
    sync() {
      if (!anchored) return;
      render(now());
    },
    setGold(gold, durable) {
      // Durability no longer changes presentation now that the reservation nudge and
      // legacy HUD gold row are gone; retain the parameter as part of the wire handle.
      void durable;
      if (toolbarGold) {
        toolbarGold.textContent = formatGold(gold);
        toolbarGold.hidden = false;
      }
    },
    setSuppressed(next) {
      suppressed = next;
      syncVisibility();
    },
  };
}
